/** Copyright 2024 SAP SE or an SAP affiliate company. All rights reserved. */
import ModalLayout from '@/components/modalLayout/ModalLayout';
import Settings from '@/components/settings/Settings';
import '@/controls/ShellPopOver/ShellPopOver.css';
import { SignOut } from '@/controls/ShellPopOver/SignOut/SignOut';
import useEnterKey from '@/hooks/useEnterKey';
import useOpen from '@/hooks/useOpen';
import { useTranslations } from '@/hooks/useTranslations';
import { createStyles } from '@/utils/styles';
import { List, ListItemStandard, ListItemStandardDomRef, Popover, PopoverPropTypes, Text, Title } from '@ui5/webcomponents-react';
import { ThemingParameters } from '@ui5/webcomponents-react-base';
import { useRef } from 'react';

type Props = {
    title?: string;
    onSignOut: () => void;
} & PopoverPropTypes;

export default function ShellPopover({ onSignOut, title, open, onClose }: Props) {
    const [isOpen, toggle] = useOpen();
    const settingItemRef = useRef<ListItemStandardDomRef>(null);

    const { t } = useTranslations();
    useEnterKey(toggle, settingItemRef);

    return (
        <Popover onClose={onClose} open={open} opener="openPopoverBtn" data-testid="shell-popover-wrapper" placement="Bottom">
            <List>
                <ListItemStandard style={styles.listItem} type="Inactive">
                    <Title data-testid="shell-popover-title-wrapper" wrappingType="None" size="H6" title={title}>
                        {title}
                    </Title>
                </ListItemStandard>

                <ListItemStandard ref={settingItemRef} style={styles.listItem} onClick={toggle} icon="action-settings">
                    <Text style={styles.listItemLabel}>{t('SETTINGS_BUTTON')}</Text>
                </ListItemStandard>

                <ModalLayout headerText={t('SETTINGS_BUTTON')} onClose={toggle} isShowDialog={isOpen}>
                    <Settings />
                </ModalLayout>

                <SignOut data-testid="sign-out-button-wrapper" onOk={onSignOut} />
            </List>
        </Popover>
    );
}

const styles = createStyles({
    listItem: {
        height: ThemingParameters.sapContent_Space_M,
        cursor: 'pointer',
    },
    listItemLabel: {
        cursor: 'pointer',
    },
});
